import { Box, Button, Typography } from "@mui/material";
import { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import Loader from "src/grading/components/Loader/Loader";
import {
  healthFlagLabel,
  modificationLabel,
} from "src/grading/utils/accommodationLabels";
import {
  HistoryChange,
  HistoryEntry,
  HistoryPageArgs,
} from "src/models/history";
import { Page } from "src/models/staff";

/** What a History page query returns; RTK Query hook results fit. */
export interface HistoryPageResult {
  data?: Page<HistoryEntry>;
  isLoading: boolean;
  isError: boolean;
  isFetching?: boolean;
  refetch?: () => unknown;
}

export interface HistoryTimelineProps {
  /** Who the History is about, for sentences such as "{actor} assigned {person} to {class}". */
  subjectName: string;
  /**
   * Loads one page. It is called as a hook by each page on screen: offset 0 first, then one more
   * page per "Show more", so each page is cached and refreshed on its own.
   */
  usePage: (page: Required<HistoryPageArgs>) => HistoryPageResult;
  /** Entries per page; 20 by default. */
  pageSize?: number;
  /** Accessible name of the list; "History" by default. */
  label?: string;
}

/** Staff and student actions (data-model §2 and §7) to their sentence under `history.action`. */
const ACTIONS: Record<string, string> = {
  invite: "invite",
  "invite-resend": "inviteResend",
  "invite-revoke": "inviteRevoke",
  "invite-accept": "inviteAccept",
  "role-change": "roleChange",
  "school-change": "schoolChange",
  "class-assign": "classAssign",
  "class-unassign": "classUnassign",
  "password-reset": "passwordReset",
  create: "create",
  update: "update",
  deactivate: "deactivate",
  reactivate: "reactivate",
  delete: "delete",
  accommodation: "accommodation",
  enroll: "enroll",
  withdraw: "withdraw",
  move: "move",
  // District history (research R21); the change lines carry old and new values or the outcome.
  "privacy.updated": "privacyUpdated",
  "cleanup.dob_cleared": "dobCleared",
  "cleanup.guardian_contacts_cleared": "guardianContactsCleared",
  "cleanup.letter_recipients_cleared": "letterRecipientsCleared",
  "cleanup.videos_purged": "videosPurged",
  "cleanup.student_sign_out": "studentSignOut",
  "district.updated": "districtUpdated",
  "license.changed": "licenseChanged",
  "mfa.recorded": "mfaRecorded",
  "mfa.withdrawn": "mfaWithdrawn",
  "dpa.recorded": "dpaRecorded",
};

/** Actions whose sentence already says what changed, so no change lines follow. */
const SELF_DESCRIBING = new Set([
  "invite",
  "invite-resend",
  "invite-revoke",
  "invite-accept",
  "role-change",
  "school-change",
  "class-assign",
  "class-unassign",
  "deactivate",
  "reactivate",
  "password-reset",
  "enroll",
  "withdraw",
  "move",
  "delete",
]);

const isEmpty = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === "" ||
  (Array.isArray(value) && value.length === 0);

/** A stored value as a reader would say it: role names, Yes/No, school names, "—" when empty. */
export const historyValue = (
  t: TFunction,
  field: string,
  value: unknown,
): string => {
  if (isEmpty(value)) return t("history.values.empty");
  if (Array.isArray(value))
    return value.map((item) => historyValue(t, field, item)).join(", ");
  if (typeof value === "boolean")
    return value ? t("history.values.yes") : t("history.values.no");
  if (typeof value === "object") {
    return Object.entries(value as Record<string, unknown>)
      .map(
        ([key, item]) =>
          `${t(`history.fields.${key}`, { defaultValue: key })}: ${historyValue(t, key, item)}`,
      )
      .join(", ");
  }
  const text = String(value);
  switch (field) {
    case "roles":
      return t(`roles.${text}`, { defaultValue: text });
    case "role":
      return t(`staff.assignmentRole.${text}`, {
        defaultValue: t(`roles.${text}`, { defaultValue: text }),
      });
    case "benchmarkSex":
      return t(`classes.sex${text}`, { defaultValue: text });
    case "status":
      // The copy check forbids the word in catalog keys, so a stopped job reads as "stopped".
      return t(
        `history.values.status.${text === "failed" ? "stopped" : text}`,
        {
          defaultValue: t(`console.status.${text}`, { defaultValue: text }),
        },
      );
    case "letterDelivery":
      return t(`history.values.letterDelivery.${text}`, { defaultValue: text });
    case "source":
      return t(`people.source.${text}`, { defaultValue: text });
    case "plannedModification":
      return modificationLabel(t, text);
    case "healthFlags":
      return healthFlagLabel(t, text);
    default:
      return text;
  }
};

const fieldName = (t: TFunction, field: string) =>
  t(`history.fields.${field}`, { defaultValue: field });

/** Who made the change, at the start of a sentence: a name, "Roster sync" or "System". */
const actorLabel = (t: TFunction, entry: HistoryEntry) => {
  if (entry.actorType === "roster-sync") return t("history.actor.rosterSync");
  if (entry.actorType === "system") return t("history.actor.system");
  if (!entry.actorName) return t("history.actor.unknown");
  return entry.actorDeactivated
    ? t("history.actor.deactivated", { name: entry.actorName })
    : entry.actorName;
};

/** The same actor after "by": "by roster sync", "by J. Chen (deactivated)". */
const actorAfterBy = (t: TFunction, entry: HistoryEntry) => {
  if (entry.actorType === "roster-sync") return t("history.by.rosterSync");
  if (entry.actorType === "system") return t("history.by.system");
  return actorLabel(t, entry);
};

const changeOf = (entry: HistoryEntry, field: string) =>
  entry.changes.find((change) => change.field === field);

/** "Grade changed 4 → 5 by roster sync", or the set and cleared forms. */
const changeSentence = (
  t: TFunction,
  entry: HistoryEntry,
  change: HistoryChange,
) => {
  const values = {
    field: fieldName(t, change.field),
    from: historyValue(t, change.field, change.from),
    to: historyValue(t, change.field, change.to),
    actor: actorAfterBy(t, entry),
  };
  if (isEmpty(change.from)) return t("history.fieldSet", values);
  if (isEmpty(change.to)) return t("history.fieldCleared", values);
  return t("history.fieldChange", values);
};

/** The plain-language sentence for one History entry. */
export const historySentence = (
  t: TFunction,
  entry: HistoryEntry,
  subjectName: string,
): string => {
  const actor = actorLabel(t, entry);
  const person = subjectName;
  const className = entry.context?.className ?? "";
  const role = entry.context?.role
    ? historyValue(t, "roles", entry.context.role)
    : "";

  if (entry.action === "update" && entry.changes.length === 1)
    return changeSentence(t, entry, entry.changes[0]);

  const fromTo = (field: string) => {
    const change = changeOf(entry, field);
    return {
      from: historyValue(t, field, change?.from),
      to: historyValue(t, field, change?.to),
    };
  };

  switch (entry.action) {
    case "invite":
      return role
        ? t("history.action.invite", { actor, person, role })
        : t("history.action.inviteNoRole", { actor, person });
    case "role-change":
      return t("history.action.roleChange", {
        actor,
        person,
        ...fromTo("roles"),
      });
    case "school-change":
      return t("history.action.schoolChange", {
        actor,
        person,
        ...fromTo("organizationId"),
      });
    case "class-assign":
      if (changeOf(entry, "role"))
        return t("history.action.classRoleChange", {
          actor,
          person,
          class: className,
          ...fromTo("role"),
        });
      return t(
        entry.context?.role === "aide"
          ? "history.action.classAssignAide"
          : "history.action.classAssign",
        { actor, person, class: className },
      );
    case "move":
      return t("history.action.move", {
        actor,
        person,
        ...fromTo("className"),
      });
    default: {
      const key = ACTIONS[entry.action];
      return key
        ? t(`history.action.${key}`, { actor, person, class: className })
        : t("history.action.other", { actor, person });
    }
  }
};

const HistoryItem = ({
  entry,
  subjectName,
}: {
  entry: HistoryEntry;
  subjectName: string;
}) => {
  const { t, i18n } = useTranslation();
  const at = new Date(entry.at);
  const showChanges =
    !SELF_DESCRIBING.has(entry.action) &&
    !(entry.action === "update" && entry.changes.length === 1) &&
    entry.changes.length > 0;
  return (
    <Box
      component="li"
      sx={{
        py: 1.25,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Typography sx={{ fontSize: 14 }}>
        {historySentence(t, entry, subjectName)}
      </Typography>
      <Typography
        component="time"
        dateTime={entry.at}
        sx={{ display: "block", fontSize: 13, color: "text.secondary" }}
      >
        {Number.isNaN(at.getTime())
          ? entry.at
          : at.toLocaleString(i18n.language, {
              dateStyle: "medium",
              timeStyle: "short",
            })}
      </Typography>
      {entry.acknowledged ? (
        <Typography sx={{ fontSize: 13, fontWeight: 700, mt: 0.25 }}>
          {t("history.acknowledged")}
        </Typography>
      ) : null}
      {showChanges ? (
        <Box
          component="ul"
          sx={{ m: 0, mt: 0.5, pl: 2.5, fontSize: 13, color: "text.secondary" }}
        >
          {entry.changes.map((change) => (
            <li key={change.field}>
              {t("history.changeLine", {
                field: fieldName(t, change.field),
                from: historyValue(t, change.field, change.from),
                to: historyValue(t, change.field, change.to),
              })}
            </li>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

/** One page of entries; the last page on screen offers "Show more" while more remain. */
const HistoryPageItems = ({
  offset,
  limit,
  last,
  subjectName,
  usePage,
  onShowMore,
}: {
  offset: number;
  limit: number;
  last: boolean;
  subjectName: string;
  usePage: HistoryTimelineProps["usePage"];
  onShowMore: () => void;
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, refetch } = usePage({ limit, offset });
  if (isLoading)
    return (
      <Box component="li" sx={{ listStyle: "none" }}>
        <Loader variant={offset === 0 ? "centered" : "inline"} />
      </Box>
    );
  if (isError || !data)
    return (
      <Box component="li" sx={{ listStyle: "none", py: 1 }}>
        <InlineAlert
          severity="error"
          action={
            refetch ? (
              <Button color="inherit" size="small" onClick={() => refetch()}>
                {t("common.retry")}
              </Button>
            ) : undefined
          }
        >
          {t("history.loadError")}
        </InlineAlert>
      </Box>
    );
  if (offset === 0 && data.items.length === 0)
    return (
      <Box component="li" sx={{ listStyle: "none" }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {t("history.empty")}
        </Typography>
      </Box>
    );
  const more = last && data.total > offset + data.items.length;
  return (
    <>
      {data.items.map((entry) => (
        <HistoryItem key={entry.id} entry={entry} subjectName={subjectName} />
      ))}
      {more ? (
        <Box component="li" sx={{ listStyle: "none", pt: 1.5 }}>
          <Button
            variant="outlined"
            onClick={onShowMore}
            data-testid="history-more"
          >
            {t("history.showMore")}
          </Button>
        </Box>
      ) : null}
    </>
  );
};

/**
 * Staff or student History as plain-language sentences, newest first (contracts/people-console.md):
 * actor names, "(deactivated)" after actors who have been deactivated, "Roster sync" and "System"
 * for automatic changes, and "Show more" paging.
 */
export const HistoryTimeline = ({
  subjectName,
  usePage,
  pageSize = 20,
  label,
}: HistoryTimelineProps) => {
  const { t } = useTranslation();
  const [pages, setPages] = useState(1);
  return (
    <Box
      component="ul"
      aria-label={label ?? t("history.title")}
      sx={{ m: 0, p: 0, listStyle: "none" }}
    >
      {Array.from({ length: pages }, (_, index) => (
        <HistoryPageItems
          key={index}
          offset={index * pageSize}
          limit={pageSize}
          last={index === pages - 1}
          subjectName={subjectName}
          usePage={usePage}
          onShowMore={() => setPages((count) => count + 1)}
        />
      ))}
    </Box>
  );
};

export default HistoryTimeline;
